import { useState, useEffect, useRef } from 'react';

const CatchphraserPresentation = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const intervalRef = useRef(null);
    const slideDuration = 5000;
    
    // Detect mobile screen
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 640); // sm breakpoint
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

 const slides = [
    // Slide 0 - Title
    {
        title: "",
        content: (
            <div className="text-center space-y-6 sm:space-y-8 py-6 sm:py-12 px-2">
                <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
                    <a 
                        href="https://catchphraser.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-2.5 py-1 sm:px-3 sm:py-1.5 bg-white/90 backdrop-blur-sm text-blue-600 rounded-lg hover:shadow-md transition-all text-xs font-medium border border-blue-200 hover:bg-white"
                    >
                        <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        catchphraser.com
                    </a>
                </div>
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 font-mono mb-2">
                    The<br />Catchphraser
                </h1>
                <div className="w-40 sm:w-64 h-1.5 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto"></div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mt-6 sm:mt-8">
                    The Pronunciation Gym
                </h2>
                <div className="mt-10 sm:mt-16 text-sm sm:text-lg text-gray-500">
                    Press spacebar or → to continue
                </div>
            </div>
        ),
        bgColor: "bg-gradient-to-br from-blue-50 via-white to-purple-50"
    },
    
    // Slide 1 - The Problem
   {
    title: "Beyond vocabulary and grammar",
    content: (
        <div className="space-y-10 py-10 px-6 md:px-8 text-center max-w-3xl mx-auto">
            <div className="text-6xl mb-8">❓</div>
            <h3 className="text-4xl font-bold text-gray-900 mb-6">
                You know the words, but...
            </h3>
            
            <div className="space-y-6 max-w-2xl mx-auto">
                <div className="flex items-start text-left">
                    <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-lg mr-4 flex-shrink-0">•</div>
                    <div className="text-xl text-gray-700 pt-1">
                        Native speakers often ask you to repeat
                    </div>
                </div>
                
                <div className="flex items-start text-left">
                    <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-lg mr-4 flex-shrink-0">•</div>
                    <div className="text-xl text-gray-700 pt-1">
                        Real conversations move faster than you can process
                    </div>
                </div>
            </div>
            
            <div className="mt-10 max-w-3xl mx-auto">
                <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl p-6 md:p-8 shadow-xl border-2 border-blue-300">
                    <div className="flex flex-col md:flex-row items-center justify-between">
                        <div className="text-4xl mb-4 md:mb-0 md:mr-6">🎯</div>
                        <div className="flex-1 text-center md:text-left">
                            <div className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                                Pronunciation is physical skill
                            </div>
                            <div className="text-lg text-gray-700 leading-relaxed">
                                Your ear and vocal muscles need systematic practice, 
                                just like building strength at the gym or learning a musical instrument.
                            </div>
                        </div>
                        <div className="text-4xl mt-4 md:mt-0 md:ml-6">💪</div>
                    </div>
                </div>
            </div>
        </div>
    ),
    bgColor: "bg-gradient-to-br from-white to-blue-50"
},
    
    // Slide 2 - The overlooked element
  {
    title: "Beyond Single Words",
    content: (
        <div className="space-y-8 py-8 px-6 text-center max-w-4xl mx-auto">
            {/* Header - Compact */}
            <div className="space-y-3">
                <div className="text-5xl">🎵</div>
                <h3 className="text-3xl font-bold text-gray-900">
                    Another Missing Piece: Prosody
                </h3>
            </div>
            
            {/* Problem & Impact Cards - Tighter */}
            <div className="grid md:grid-cols-2 gap-5 mb-6">
                {/* Problem Card */}
                <div className="bg-gradient-to-br from-red-50 to-white rounded-xl p-5 border-2 border-red-200 shadow-sm">
                    <div className="flex items-center space-x-2 mb-3">
                        <div className="text-xl">❌</div>
                        <h4 className="text-lg font-bold text-gray-900">The Problem</h4>
                    </div>
                    <div className="space-y-2 text-left">
                        <div className="flex items-center">
                            <div className="w-5 h-5 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs mr-2 flex-shrink-0">!</div>
                            <span className="text-sm font-medium text-gray-900">Rarely taught systematically</span>
                        </div>
                        <div className="flex items-center">
                            <div className="w-5 h-5 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs mr-2 flex-shrink-0">!</div>
                            <span className="text-sm font-medium text-gray-900">Left to "pick up" intuitively</span>
                        </div>
                    </div>
                </div>
                
                {/* Impact Card */}
                <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border-2 border-blue-200 shadow-sm">
                    <div className="flex items-center space-x-2 mb-3">
                        <div className="text-xl">💥</div>
                        <h4 className="text-lg font-bold text-gray-900">The Impact</h4>
                    </div>
                    <div className="space-y-2 text-left">
                        <div className="flex items-center">
                            <div className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs mr-2 flex-shrink-0">•</div>
                            <span className="text-sm font-medium text-gray-900">Harder to be understood</span>
                        </div>
                        <div className="flex items-center">
                            <div className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs mr-2 flex-shrink-0">•</div>
                            <span className="text-sm font-medium text-gray-900">Harder to understand others</span>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Key Definition - More Compact */}
            <div className="mb-6">
                <div className="text-xl font-bold text-gray-900 mb-1">
                    Rhythm, stress & intonation patterns
                </div>
                <div className="text-base text-gray-700 max-w-2xl mx-auto">
                    The musicality of speech. 
                    <span className="font-medium text-gray-900"> Encodes meaning beyond words—signaling grammar, focus, intent, and emphasis.</span>
                </div>
            </div>
            
            {/* Conclusion - Compact but Prominent */}
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-5 shadow-lg border-2 border-purple-300">
                <div className="flex items-center justify-between">
                    <div className="text-2xl">🎯</div>
                    <div className="text-center flex-1 px-3">
                        <div className="text-xl font-bold text-gray-900 mb-0.5">
                            Must be trained, not just explained
                        </div>
                        <div className="text-sm text-gray-700">
                            Like musical rhythm—requires deliberate practice to master
                        </div>
                    </div>
                    <div className="text-2xl">⏱️</div>
                </div>
            </div>
        </div>
    ),
    bgColor: "bg-gradient-to-br from-white to-purple-50"
},
    
    // Slide 3 - Our Vision
    {
        title: "Our Vision",
        content: (
            <div className="space-y-10 py-10 px-2 text-center max-w-4xl mx-auto">
                <div className="text-center">
                    <div className="text-5xl md:text-6xl mb-6">🎯</div>
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                        Make Pronunciation<br />Trainable
                    </h3>
                </div>
                
                <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                    <div className="text-center">
                        <div className="text-5xl mb-4">💪</div>
                        <h4 className="text-xl font-bold text-gray-900 mb-3">
                            Like Fitness
                        </h4>
                        <p className="text-base text-gray-600">
                            Structured exercises & muscle memory
                        </p>
                    </div>
                    
                    <div className="text-center">
                        <div className="text-5xl mb-4">🎸</div>
                        <h4 className="text-xl font-bold text-gray-900 mb-3">
                            Like Music Instrument
                        </h4>
                        <p className="text-base text-gray-600">
                            Rhythm, timing & technical skill
                        </p>
                    </div>
                    
                    <div className="text-center">
                        <div className="text-5xl mb-4">📊</div>
                        <h4 className="text-xl font-bold text-gray-900 mb-3">
                            Measurable
                        </h4>
                        <p className="text-base text-gray-600">
                            Precise feedback & progress tracking
                        </p>
                    </div>
                </div>
                
                <div className="mt-10 text-center">
                    <div className="inline-block bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl p-4 px-8">
                        <div className="text-lg text-gray-700 font-medium">
                            Systematic development through guided practice & repetition
                        </div>
                    </div>
                </div>
            </div>
        ),
        bgColor: "bg-gradient-to-br from-blue-50 to-purple-50"
    },
    
    // Slide 4 - The Core Method
    {
    title: "The Core Method",
    content: (
        <div className="space-y-8 py-8 px-4 text-center max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="text-5xl mb-4">👥</div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">
                    Shadowing
                </h3>
                <div className="text-xl text-gray-600 mb-2">
                    Listen → Repeat (simultaneously or separately) → Check & Correct → Internalize
                </div>
                <div className="text-base text-gray-500 italic">
                    (Trains both ear and vocal muscles)
                </div>
            </div>
            
            {/* Two Column Content */}
            <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto mb-8">
                {/* Human Narrators */}
                <div className="text-center">
                    <div className="text-4xl mb-3">🗣️</div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                        Human Narrators
                    </h4>
                    <p className="text-base text-gray-600">
                        Natural rhythm, stress & intonation
                    </p>
                </div>
                
                {/* AI Analysis */}
                <div className="text-center">
                    <div className="text-4xl mb-3">🤖</div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                        AI Analysis
                    </h4>
                    <p className="text-base text-gray-600">
                        Spaced repetition & adaptive training
                    </p>
                </div>
            </div>
            
            {/* Key Insight */}
            <div className="bg-gradient-to-r from-blue-100 to-cyan-100 rounded-xl p-5 shadow-lg border border-blue-200">
                <div className="flex items-center justify-center">
                    <div className="text-2xl mr-3">💪</div>
                    <div className="text-left">
                        <div className="text-lg font-bold text-gray-900 mb-0.5">
                            Builds vocal muscle memory
                        </div>
                        <div className="text-sm text-gray-700">
                            Like spaced repetition for vocabulary—but for your voice
                        </div>
                    </div>
                </div>
            </div>
        </div>
    ),
    bgColor: "bg-gradient-to-br from-blue-50 to-cyan-50"
},
    
    // Slide 5 - Pronunciation Gym
    {
        title: "Pronunciation Gym",
        content: (
            <div className="space-y-8 py-8 px-2 text-center max-w-4xl mx-auto">
                <div className="text-center mb-6">
                    <div className="text-5xl mb-4">🏋️‍♂️</div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-3">
                        The Three Training Stations
                    </h3>
                    <p className="text-lg text-gray-600">
                        Systematic approach to pronunciation mastery
                    </p>
                </div>
                
                <div className="grid md:grid-cols-3 gap-5 max-w-4xl mx-auto">
                    <div className="bg-gradient-to-b from-green-50 to-white rounded-xl p-4 border-2 border-green-200 shadow text-center">
                        <div className="mb-4">
                            <div className="text-4xl mb-3">👄</div>
                            <h4 className="text-lg font-bold text-gray-900 mb-3">
                                Articulation Station
                            </h4>
                        </div>
                        
                        <div className="space-y-3 text-left">
                            <div className="flex items-start">
                                <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">✓</div>
                                <span className="text-sm text-gray-700">
                                    Lips, tongue & vocal tract coordination
                                </span>
                            </div>
                            <div className="flex items-start">
                                <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">✓</div>
                                <span className="text-sm text-gray-700">
                                    Produce unfamiliar sounds
                                </span>
                            </div>
                            <div className="flex items-start">
                                <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">✓</div>
                                <span className="text-sm text-gray-700">
                                    Override native language patterns
                                </span>
                            </div>
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-green-100">
                            <div className="text-sm text-green-700 font-medium">
                                Fine vocal motorics
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-gradient-to-b from-purple-50 to-white rounded-xl p-4 border-2 border-purple-200 shadow text-center">
                        <div className="mb-4">
                            <div className="text-4xl mb-3">🎵</div>
                            <h4 className="text-lg font-bold text-gray-900 mb-3">
                                Prosody Station
                            </h4>
                        </div>
                        
                        <div className="space-y-3 text-left">
                            <div className="flex items-start">
                                <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">🎶</div>
                                <span className="text-sm text-gray-700">
                                    Rhythm, stress & intonation
                                </span>
                            </div>
                            <div className="flex items-start">
                                <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">🎶</div>
                                <span className="text-sm text-gray-700">
                                    Speech melody & timing
                                </span>
                            </div>
                            <div className="flex items-start">
                                <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">🎶</div>
                                <span className="text-sm text-gray-700">
                                    Natural-sounding speech flow
                                </span>
                            </div>
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-purple-100">
                            <div className="text-sm text-purple-700 font-medium">
                                40% of comprehension
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-gradient-to-b from-blue-50 to-white rounded-xl p-4 border-2 border-blue-200 shadow text-center">
                        <div className="mb-4">
                            <div className="text-4xl mb-3">🎧</div>
                            <h4 className="text-lg font-bold text-gray-900 mb-3">
                                Perception Station
                            </h4>
                        </div>
                        
                        <div className="space-y-3 text-left">
                            <div className="flex items-start">
                                <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">🎯</div>
                                <span className="text-sm text-gray-700">
                                    Phonemic contrast recognition
                                </span>
                            </div>
                            <div className="flex items-start">
                                <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">🎯</div>
                                <span className="text-sm text-gray-700">
                                    Sound category internalization
                                </span>
                            </div>
                            <div className="flex items-start">
                                <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">🎯</div>
                                <span className="text-sm text-gray-700">
                                    Auditory perception training
                                </span>
                            </div>
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-blue-100">
                            <div className="text-sm text-blue-700 font-medium">
                                Ear training like a musician
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="mt-8 text-center">
                    <div className="inline-block bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-4 px-6 shadow">
                        <div className="flex items-center space-x-3">
                            <div className="text-2xl">💪</div>
                            <div>
                                <div className="text-base font-bold text-gray-900">
                                    Complete pronunciation training
                                </div>
                                <div className="text-sm text-gray-600">
                                    From muscle memory to natural communication
                                </div>
                            </div>
                            <div className="text-2xl">🎸</div>
                        </div>
                    </div>
                </div>
            </div>
        ),
        bgColor: "bg-gradient-to-br from-white to-gray-50"
    },
    
    // Slide 6 - Sound-Level Feedback
    {
        title: "Sound-Level Feedback",
        content: (
            <div className="space-y-6 py-6 px-2 text-center max-w-2xl mx-auto">
                <div className="text-center mb-4">
                    <div className="text-5xl mb-4">🎯</div>
                    <h3 className="text-3xl font-bold text-gray-900">
                        Visual Pronunciation Scoring
                    </h3>
                    <p className="text-lg text-gray-600 mt-2">
                        See accuracy for each sound you produce
                    </p>
                </div>
                
                <div className="max-w-2xl mx-auto">
                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                        <img 
                            alt="Phoneme accuracy feedback interface" 
                            className="w-full rounded-lg"
                            src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/kattenspiserikkefisk.png`} 
                        />
                    </div>
                    
                    <div className="mt-4 text-center">
                        <div className="font-mono text-lg text-gray-700 tracking-wider bg-gray-100 p-3 rounded-lg inline-block overflow-x-auto max-w-full">
                            /ˈkɑtːən ˈspiːsər ɪkːə fɪsk/
                        </div>
                    </div>
                </div>
            </div>
        ),
        bgColor: "bg-gradient-to-br from-white to-green-50"
    },
    
    // Slide 7 - Pitch Analysis
    {
        title: "Pitch Analysis",
        content: (
            <div className="space-y-6 py-6 px-2 text-center max-w-2xl mx-auto">
                <div className="text-center mb-4">
                    <div className="text-5xl mb-4">📈</div>
                    <h3 className="text-3xl font-bold text-gray-900">
                        Intonation Pattern Visualization
                    </h3>
                    <p className="text-lg text-gray-600 mt-2">
                        Visualize pitch contours for pronunciation training
                    </p>
                </div>
                
                <div className="max-w-2xl mx-auto">
                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                        <img 
                            alt="Pitch contour visualization" 
                            className="w-full rounded-lg"
                            src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/kattenspiserikkefisk_pitch.png`} 
                        />
                    </div>
                    
                    <div className="mt-6 text-center">
                        <div className="inline-block bg-gradient-to-r from-purple-100 to-blue-100 rounded-xl p-4 px-6 shadow">
                            <div className="text-lg font-bold text-gray-900 mb-2">
                                Master intonation patterns visually
                            </div>
                            <div className="text-base text-gray-700">
                                Perfect for languages where tone changes meaning
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        ),
        bgColor: "bg-gradient-to-br from-white to-purple-50"
    },
    
    // Slide 8 - Learning Materials
  {
    title: "Learning Materials",
    content: (
        <div className="space-y-8 py-8 px-4 text-center max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-gray-900">
                    Purpose-Built Content
                </h3>
            </div>
            
            {/* Three Column Content */}
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
                {/* Pronunciation Courses */}
                <div className="text-center">
                    <div className="text-4xl mb-3">📚</div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                        Pronunciation Courses
                    </h4>
                    <p className="text-sm text-gray-600">
                        Structured paths for progressive skill building
                    </p>
                </div>
                
                {/* Shadowing Audio */}
                <div className="text-center">
                    <div className="text-4xl mb-3">🎧</div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                        Shadowing Audio
                    </h4>
                    <p className="text-sm text-gray-600">
                        Optimized for repetition & muscle memory
                    </p>
                </div>
                
                {/* Authentic Content */}
                <div className="text-center">
                    <div className="text-4xl mb-3">🎙️</div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                        Authentic Content
                    </h4>
                    <p className="text-sm text-gray-600">
                        Podcasts, audiobooks & real speech for natural rhythm, stress & intonation
                    </p>
                </div>
            </div>
            
            {/* Key Insight */}
            <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl p-5 shadow-lg border border-blue-200">
                <div className="flex items-center justify-center">
                    <div className="text-2xl mr-3">💡</div>
                    <div className="text-left">
                        <div className="text-lg font-bold text-gray-900 mb-0.5">
                            From Practice to Real-World Communication
                        </div>
                        <div className="text-sm text-gray-700">
                            Bridge the gap between exercises and fluent, natural conversation
                        </div>
                    </div>
                </div>
            </div>
        </div>
    ),
    bgColor: "bg-gradient-to-br from-white to-blue-50"
},
    // Slide 9 - Summary
    {
    title: "Summary",
    content: (
        <div className="space-y-8 py-8 px-4 text-center max-w-3xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="text-5xl mb-4">🎯</div>
                <h3 className="text-3xl font-bold text-gray-900">
                    Pronunciation as Disciplined Practice
                </h3>
            </div>
            
            {/* Two Column Content */}
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-8">
                {/* Train it like a sport */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border-2 border-green-200 shadow-sm">
                    <div className="text-4xl mb-3">💪</div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                        Train it like a sport
                    </h4>
                    <p className="text-sm text-gray-600">
                        Muscle memory & repetition
                    </p>
                </div>
                
                {/* Refine it like an instrument */}
                <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-5 border-2 border-purple-200 shadow-sm">
                    <div className="text-4xl mb-3">🎸</div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                        Refine it like an instrument
                    </h4>
                    <p className="text-sm text-gray-600">
                        Technical skill & ear training
                    </p>
                </div>
            </div>
            
            {/* The Goal */}
            <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl p-6 shadow-lg border border-blue-200 mb-6">
                <div className="flex items-center justify-center">
                    <div className="text-2xl mr-3">💡</div>
                    <div className="text-left">
                        <div className="text-lg font-bold text-gray-900 mb-1">
                            The Goal
                        </div>
                        <div className="text-sm text-gray-700">
                            Bridge the gap between knowledge and natural communication
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Final Statement */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-5 border-2 border-blue-300 shadow-md">
                <div className="text-center">
                    <div className="text-xl font-bold text-gray-900">
                        Transform pronunciation into a trainable, measurable skill
                    </div>
                </div>
            </div>
        </div>
    ),
    bgColor: "bg-gradient-to-br from-blue-50 to-purple-50"
},
    
    // Slide 10 - The Team
    {
        title: "The Team",
        content: (
            <div className="space-y-8 py-8 px-2 text-center max-w-5xl mx-auto">
                <div className="text-center mb-6">
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">
                        The Catchphraser Team
                    </h3>
                    <p className="text-lg text-gray-600">
                        Language learning enthusiasts helping you master pronunciation
                    </p>
                </div>
                
                <div className="grid lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200 space-y-6">
                        <div className="text-center">
                            <div className="relative group">
                                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-4 border-white shadow-lg mx-auto mb-4">
                                    <img 
                                        src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/it.jpg`}
                                        alt="Dr. Inna Tolskaya"
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                    <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold hidden">
                                        IT
                                    </div>
                                </div>
                            </div>
                            <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-1">Dr. Inna Tolskaya</h3>
                            <p className="text-sm text-blue-600 font-medium mb-1">Learning & Communication</p>
                            <p className="text-xs text-gray-600">Pronunciation & Teaching</p>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="flex items-center">
                                <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm mr-3">💫</div>
                                <h3 className="text-md font-semibold text-gray-800">My Focus</h3>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    Passionate about helping learners find their voice in new languages. Focuses on creating supportive learning environments where pronunciation practice feels natural and empowering.
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-5 border border-purple-200 space-y-6">
                        <div className="text-center">
                            <div className="relative group">
                                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-4 border-white shadow-lg mx-auto mb-4">
                                    <img 
                                        src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/ds.jpg`}
                                        alt="Dr. Dmitry Shcherbin"
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                    <div className="w-full h-full bg-gradient-to-br from-purple-400 to-violet-500 rounded-full flex items-center justify-center text-white text-2xl font-bold hidden">
                                        DS
                                    </div>
                                </div>
                            </div>
                            <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-1">Dr. Dmitry Shcherbin</h3>
                            <p className="text-sm text-purple-600 font-medium mb-1">Technology & Development</p>
                            <p className="text-xs text-gray-600">Building Learning Tools</p>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="flex items-center">
                                <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm mr-3">💫</div>
                                <h3 className="text-md font-semibold text-gray-800">My Focus</h3>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    Loves creating technology that actually helps people learn. Builds tools that listen carefully to how you speak and give helpful feedback on your pronunciation journey.
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200 space-y-6">
                        <div className="text-center">
                            <div className="relative group">
                                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-4 border-white shadow-lg mx-auto mb-4">
                                    <img 
                                        src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/oh.jpg`}
                                        alt="Associate professor Olaf Husby"
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                    <div className="w-full h-full bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center text-white text-2xl font-bold hidden">
                                        OH
                                    </div>
                                </div>
                            </div>
                            <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-1">Associate professor Olaf Husby</h3>
                            <p className="text-sm text-green-600 font-medium mb-1">Language & Research</p>
                            <p className="text-xs text-gray-600">Sounds & Teaching Methods</p>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="flex items-center">
                                <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm mr-3">💫</div>
                                <h3 className="text-md font-semibold text-gray-800">My Focus</h3>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    Dedicated to understanding how people learn pronunciation. Works on making language learning more effective by focusing on the rhythm and music of speech.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-gray-200 max-w-2xl mx-auto">
                    <div className="text-center text-gray-700">
                        <div className="text-lg font-medium mb-2">What We Believe</div>
                        <div className="text-sm text-gray-600">
                            Good pronunciation isn't just about sounds—it's about the music and flow of your speech.
                            Everyone improves with regular practice and helpful feedback. Feeling good about how you 
                            sound helps you communicate more freely.
                        </div>
                    </div>
                </div>
            </div>
        ),
        bgColor: "bg-gradient-to-br from-white to-blue-50"
    }
];

    // Auto-play functionality
    const playSlideshow = () => {
        if (isPlaying) {
            clearInterval(intervalRef.current);
            setIsPlaying(false);
        } else {
            setIsPlaying(true);
            intervalRef.current = setInterval(() => {
                setCurrentSlide(current => {
                    if (current < slides.length - 1) {
                        return current + 1;
                    } else {
                        clearInterval(intervalRef.current);
                        setIsPlaying(false);
                        return current;
                    }
                });
            }, slideDuration);
        }
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event) => {
            switch(event.key) {
                case 'ArrowLeft':
                    event.preventDefault();
                    if (currentSlide > 0) prevSlide();
                    break;
                case 'ArrowRight':
                case ' ':
                    event.preventDefault();
                    if (currentSlide < slides.length - 1) nextSlide();
                    break;
                case 'p':
                case 'P':
                    event.preventDefault();
                    playSlideshow();
                    break;
                case 'Home':
                    event.preventDefault();
                    goToSlide(0);
                    break;
                case 'End':
                    event.preventDefault();
                    goToSlide(slides.length - 1);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentSlide, isPlaying]);

    useEffect(() => {
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, []);

    const nextSlide = () => {
        if (currentSlide < slides.length - 1) {
            setCurrentSlide(currentSlide + 1);
            if (isPlaying) {
                clearInterval(intervalRef.current);
                intervalRef.current = setInterval(() => {
                    setCurrentSlide(current => {
                        if (current < slides.length - 1) return current + 1;
                        clearInterval(intervalRef.current);
                        setIsPlaying(false);
                        return current;
                    });
                }, slideDuration);
            }
        }
    };

    const prevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
            if (isPlaying) {
                clearInterval(intervalRef.current);
                intervalRef.current = setInterval(() => {
                    setCurrentSlide(current => {
                        if (current < slides.length - 1) return current + 1;
                        clearInterval(intervalRef.current);
                        setIsPlaying(false);
                        return current;
                    });
                }, slideDuration);
            }
        }
    };

    const goToSlide = (index) => {
        setCurrentSlide(index);
        if (isPlaying) {
            clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => {
                setCurrentSlide(current => {
                    if (current < slides.length - 1) return current + 1;
                        clearInterval(intervalRef.current);
                        setIsPlaying(false);
                        return current;
                    });
                }, slideDuration);
            }
        };

        // Link component for desktop (inside slide)
        const DesktopLink = () => (
            <div className="absolute top-4 right-4">
                <a 
                    href="https://catchphraser.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 bg-white/90 backdrop-blur-sm text-blue-600 rounded-lg hover:shadow-md transition-all text-xs font-medium border border-blue-200 hover:bg-white"
                >
                    <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    catchphraser.com
                </a>
            </div>
        );

        // Link component for mobile (outside slide)
        const MobileLink = () => (
            <div className="mb-3 flex justify-center sm:hidden">
                <a 
                    href="https://catchphraser.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 bg-white/90 backdrop-blur-sm text-blue-600 rounded-lg hover:shadow-md transition-all text-xs font-medium border border-blue-200 hover:bg-white shadow-sm"
                >
                    <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    catchphraser.com
                </a>
            </div>
        );

        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-3 sm:p-4 md:p-6 lg:p-8">
                <div className="max-w-6xl mx-auto">
                    {/* Mobile link - placed above slide on mobile */}
                    {isMobile && currentSlide > 0 && <MobileLink />}

                    {/* Header with controls */}
                    <div className="mb-3 sm:mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                        <div className="text-xs sm:text-sm text-gray-500 font-medium">
                            {currentSlide + 1} / {slides.length}
                        </div>
                        <div className="flex items-center space-x-3 sm:space-x-4 w-full sm:w-auto">
                            {isPlaying && (
                                <div className="flex items-center text-green-600">
                                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1 sm:mr-2"></div>
                                    <span className="text-xs sm:text-sm">Playing</span>
                                </div>
                            )}
                            <button
                                onClick={playSlideshow}
                                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium flex items-center transition-all text-xs sm:text-sm flex-1 sm:flex-none ${
                                    isPlaying 
                                        ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
                                        : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                                }`}
                            >
                                {isPlaying ? '⏸️ Pause' : '▶️ Play'}
                            </button>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="h-1.5 bg-gray-200 rounded-full mb-4 sm:mb-6 overflow-hidden">
                        <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
                            style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                        ></div>
                    </div>

                    {/* Current Slide */}
                    <div className={`rounded-xl sm:rounded-2xl shadow-xl overflow-hidden transition-all duration-300 ${slides[currentSlide].bgColor} relative`}>
                        {/* Desktop link - placed inside slide on desktop */}
                        {!isMobile && currentSlide > 0 && <DesktopLink />}
                        
                        <div className="p-4 sm:p-6 md:p-8 lg:p-12 min-h-[60vh] sm:min-h-[70vh] flex flex-col">
                            {slides[currentSlide].title && (
                                <div className="mb-4 sm:mb-6 text-center">
                                    <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 font-mono">
                                        {slides[currentSlide].title}
                                    </h2>
                                    <div className="w-16 sm:w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mt-1 sm:mt-2"></div>
                                </div>
                            )}

                            <div className="flex-grow flex items-center overflow-y-auto">
                                <div className="w-full">
                                    {slides[currentSlide].content}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Navigation */}
                    <div className="mt-4 sm:mt-6 flex justify-between items-center">
                        <button
                            onClick={prevSlide}
                            disabled={currentSlide === 0}
                            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm ${
                                currentSlide === 0 
                                    ? 'text-gray-400 cursor-not-allowed' 
                                    : 'text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                            ← Previous
                        </button>
                        
                        <div className="flex space-x-1">
                            {slides.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => goToSlide(index)}
                                    className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${
                                        currentSlide === index 
                                            ? isPlaying 
                                                ? 'bg-green-500 animate-pulse'
                                                : 'bg-blue-500' 
                                            : 'bg-gray-300 hover:bg-gray-400'
                                    }`}
                                ></button>
                            ))}
                        </div>
                        
                        <button
                            onClick={nextSlide}
                            disabled={currentSlide === slides.length - 1}
                            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm ${
                                currentSlide === slides.length - 1 
                                    ? 'text-gray-400 cursor-not-allowed' 
                                    : 'text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                            {currentSlide === slides.length - 1 ? '✓ Finish' : 'Next →'}
                        </button>
                    </div>

                    {/* Keyboard Hint */}
                    <div className="mt-3 sm:mt-4 text-center text-xs text-gray-400">
                        ← → to navigate • Space to advance • P to play/pause
                    </div>
                </div>
            </div>
        );
    };

    export default CatchphraserPresentation;